import React from "react";
import { Link, useHistory } from "react-router-dom";
import { useMerchantAuthUser } from "../auth/MerchantAuthContext";
import MerchantDatePickerField from "../components/MerchantDatePickerField";
import MerchantLineChart from "../components/MerchantLineChart";
import MerchantStatCard from "../components/MerchantStatCard";
import MerchantStatusBadge from "../components/MerchantStatusBadge";
import { MerchantDownloadIcon } from "../components/MerchantIcons";
import {
  fetchMerchantDashboardCardCountries,
  fetchMerchantDashboardPaymentMethods,
  fetchMerchantDashboardRecentTransactions,
  fetchMerchantDashboardSalesTrend,
  fetchMerchantDashboardSummary,
  fetchMerchantDashboardTopOutlets,
  downloadMerchantBlob,
  getMerchantApiErrorMessage,
  normalizeMerchantApiTransaction,
} from "../data/merchantApi";
import {
  formatMerchantPercent,
  formatMerchantPaymentMethod,
  getMerchantMethod,
  getMerchantStatus,
  getMerchantStatusLabel,
  pickMerchantField,
  toMerchantString,
} from "../data/merchantLegacyUtils";
import {
  formatMerchantCompactCurrency,
  formatMerchantCurrency,
  renderMerchantMeta,
} from "../data/merchantData";

const DASHBOARD_MONTH_OPTION_COUNT = 36;
const PAYMENT_METHOD_COLORS = {
  "payment card": "#1a56db",
  "dcs pay": "#1a56db",
  "ali pay": "#1677ff",
  "alipay+": "#1677ff",
  "wechat pay": "#16a34a",
  "dbs pay": "#92400e",
  "paynow qr": "#92400e",
  default: "#6b7280",
};

const COUNTRY_META = {
  SG: { name: "Singapore", flag: "🇸🇬" },
  KR: { name: "South Korea", flag: "🇰🇷" },
  CN: { name: "China", flag: "🇨🇳" },
  TW: { name: "Taiwan", flag: "🇨🇳" },
  JP: { name: "Japan", flag: "🇯🇵" },
  HK: { name: "Hong Kong", flag: "🇭🇰" },
  US: { name: "United States", flag: "🇺🇸" },
  AU: { name: "Australia", flag: "🇦🇺" },
};

function getMonthOptionLabel(monthValue) {
  const matchedOption = getDashboardMonthOptions().find(function findOption(option) {
    return option.value === monthValue;
  });

  if (matchedOption) {
    return matchedOption.label;
  }

  if (!monthValue) {
    return "-";
  }

  const parts = toMerchantString(monthValue).split("-");

  if (parts.length !== 2) {
    return monthValue;
  }

  return new Date(Number(parts[0]), Number(parts[1]) - 1, 1).toLocaleDateString("en-SG", {
    month: "long",
    year: "numeric",
  });
}

function formatMonthValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return year + "-" + month;
}

function createDashboardMonthOptions() {
  const today = new Date();
  const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const options = [];

  for (let index = 0; index < DASHBOARD_MONTH_OPTION_COUNT; index += 1) {
    const currentMonth = new Date(
      startMonth.getFullYear(),
      startMonth.getMonth() - index,
      1,
    );
    const value = formatMonthValue(currentMonth);

    options.push({
      value: value,
      label: currentMonth.toLocaleDateString("en-SG", {
        month: "short",
        year: "numeric",
      }),
    });
  }

  return options;
}

function getDashboardMonthOptions() {
  return createDashboardMonthOptions();
}

function getDashboardMonthDate(monthValue) {
  const matched = toMerchantString(monthValue).match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return null;
  }

  return new Date(Number(matched[1]), Number(matched[2]) - 1, 1);
}

function getDashboardMonthValueFromDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return formatMonthValue(date);
}

function getPaymentMethodColor(methodName) {
  const normalizedMethod = toMerchantString(methodName).toLowerCase();

  return PAYMENT_METHOD_COLORS[normalizedMethod] || PAYMENT_METHOD_COLORS.default;
}

function formatOutletDisplayName(name) {
  return toMerchantString(name).replace("@", " · ");
}

function buildEmptyDashboardState() {
  return {
    kpis: [
      {
        label: "Total GMV",
        value: formatMerchantCurrency(0),
        stripeClassName: "merchant-stripe-green",
        metaBadge: "merchant-badge-blue",
        metaLabel: "-",
        metaText: "selected period",
      },
      {
        label: "Transactions",
        value: "0",
        stripeClassName: "merchant-stripe-blue",
        metaBadge: "merchant-badge-green",
        metaLabel: "-",
        metaText: "transaction count",
      },
      {
        label: "Total Refunds",
        value: formatMerchantCurrency(0),
        stripeClassName: "merchant-stripe-red",
        metaBadge: "merchant-badge-amber",
        metaLabel: "-",
        metaText: "refund rate",
      },
      {
        label: "Net Settled",
        value: formatMerchantCurrency(0),
        stripeClassName: "merchant-stripe-amber",
        metaBadge: "merchant-badge-blue",
        metaLabel: "-",
        metaText: "settlement model",
      },
    ],
    chartData: [],
    paymentSections: [],
    recentTransactions: [],
    countryRows: [],
    topOutlets: [],
  };
}

function buildDashboardKpis(summary, fallbackKpis) {
  if (!summary) {
    return fallbackKpis;
  }

  return [
    {
      label: "Total GMV",
      value: formatMerchantCurrency(summary.total_gmv || 0),
      stripeClassName: "merchant-stripe-green",
      metaBadge: "merchant-badge-blue",
      metaLabel: getMonthOptionLabel(toMerchantString(summary.period)),
      metaText: "selected period",
    },
    {
      label: "Transactions",
      value: Number(summary.transaction_count || 0).toLocaleString("en-SG"),
      stripeClassName: "merchant-stripe-blue",
      metaBadge: "merchant-badge-green",
      metaLabel: "Ready",
      metaText: "transaction count",
    },
    {
      label: "Total Refunds",
      value: formatMerchantCurrency(summary.refund_amount || 0),
      stripeClassName: "merchant-stripe-red",
      metaBadge: "merchant-badge-amber",
      metaLabel: formatMerchantPercent(summary.refund_rate || 0, 2, "0.00%"),
      metaText: "refund rate",
    },
    {
      label: "Net Settled",
      value: formatMerchantCurrency(summary.net_settled_amount || 0),
      stripeClassName: "merchant-stripe-amber",
      metaBadge: "merchant-badge-blue",
      metaLabel: toMerchantString(summary.settlement_model, "-"),
      metaText: "settlement model",
    },
  ];
}

function buildChartDataFromApi(salesTrend) {
  if (!salesTrend || !Array.isArray(salesTrend.items)) {
    return [];
  }

  return salesTrend.items.map(function mapItem(item, index) {
    const labelSource = toMerchantString(item.date, String(index + 1));
    const labelParts = labelSource.split("-");

    return {
      label: labelParts.length === 3 ? String(Number(labelParts[2])) : labelSource,
      value: Number(item.gmv || 0),
    };
  });
}

function buildPaymentSectionsFromApi(paymentMethods) {
  if (!paymentMethods || !Array.isArray(paymentMethods.items)) {
    return [];
  }

  return paymentMethods.items.map(function mapItem(item) {
    const methodName = formatMerchantPaymentMethod(item.payment_method, "Payment");
    const percentage = Number(item.percentage || 0);
    const gmv = Number(item.gmv || 0);
    const color = getPaymentMethodColor(methodName);

    return {
      title:
        methodName +
        " — " +
        formatMerchantPercent(percentage, 2, "0.00%") +
        " · " +
        formatMerchantCurrency(gmv),
      items: [
        {
          name: methodName,
          percentage:
            formatMerchantPercent(percentage, 2, "0.00%") +
            " · " +
            formatMerchantCurrency(gmv),
          width: Math.max(0, Math.min(100, percentage)).toFixed(2) + "%",
          color: color,
        },
      ],
    };
  });
}

function buildCountryRowsFromApi(countryResponse) {
  if (!countryResponse || !Array.isArray(countryResponse.items)) {
    return [];
  }

  return countryResponse.items.map(function mapItem(item) {
    const countryCode = toMerchantString(item.country_code, "UNKNOWN").toUpperCase();
    const countryMeta = COUNTRY_META[countryCode] || {
      name: countryCode || "Unknown",
      flag: "🌐",
    };

    return {
      code: countryCode,
      name: countryMeta.name,
      flag: countryMeta.flag,
      gmv: Number(item.gmv || 0),
      transactionCount: Number(item.transaction_count || 0),
      percentage: Number(item.percentage || 0),
    };
  });
}

function buildTopOutletsFromApi(topOutletsResponse) {
  if (!topOutletsResponse || !Array.isArray(topOutletsResponse.items)) {
    return [];
  }

  return topOutletsResponse.items.map(function mapItem(item) {
    return {
      id: toMerchantString(item.store_id, ""),
      name: toMerchantString(item.store_name, "Unknown Outlet"),
      gmv: Number(item.gmv || 0),
      transactions: Number(item.transaction_count || 0),
    };
  });
}

function buildDashboardExportContent(state) {
  const lines = [["Section", "Label", "Value"].join(",")];

  state.kpis.forEach(function addKpiRow(kpi) {
    lines.push(["KPI", kpi.label, toMerchantString(kpi.value)].join(","));
  });

  state.recentTransactions.forEach(function addTransactionRow(transaction) {
    lines.push(
      [
        "Recent Transaction",
        toMerchantString(pickMerchantField(transaction, ["id", "txnId"]), ""),
        toMerchantString(pickMerchantField(transaction, ["amount"]), ""),
      ].join(","),
    );
  });

  return lines.join("\n");
}

export default function MerchantDashboardPage() {
  const history = useHistory();
  const merchantUser = useMerchantAuthUser();
  const emptyState = React.useMemo(buildEmptyDashboardState, []);
  const monthOptions = React.useMemo(createDashboardMonthOptions, []);
  const defaultMonthValue = monthOptions[0] ? monthOptions[0].value : "";
  const latestMonthDate = monthOptions[0]
    ? getDashboardMonthDate(monthOptions[0].value)
    : null;
  const earliestMonthDate = monthOptions.length
    ? getDashboardMonthDate(monthOptions[monthOptions.length - 1].value)
    : null;
  const [selectedMonth, setSelectedMonth] = React.useState(defaultMonthValue);
  const [dashboardState, setDashboardState] = React.useState(emptyState);
  const [pagePeriod, setPagePeriod] = React.useState(getMonthOptionLabel(defaultMonthValue));
  const [dataNote, setDataNote] = React.useState(
    "Merchant dashboard is waiting for live API data. Before the response returns, all cards keep neutral placeholder values.",
  );
  const [requestState, setRequestState] = React.useState("loading");
  const [isExporting, setIsExporting] = React.useState(false);

  React.useEffect(
    function loadDashboardData() {
      let isActive = true;

      async function requestDashboardData() {
        setRequestState("loading");
        setDataNote("Requesting merchant dashboard data from the live API…");

        const query = selectedMonth ? { month: selectedMonth } : {};
        const results = await Promise.allSettled([
          fetchMerchantDashboardSummary(query),
          fetchMerchantDashboardRecentTransactions({ limit: 7 }),
          fetchMerchantDashboardTopOutlets({ ...query, limit: 5 }),
          fetchMerchantDashboardPaymentMethods(query),
          fetchMerchantDashboardCardCountries({ ...query, limit: 8 }),
          fetchMerchantDashboardSalesTrend(query),
        ]);

        if (!isActive) {
          return;
        }

        const summaryResult = results[0];
        const recentTransactionsResult = results[1];
        const topOutletsResult = results[2];
        const paymentMethodsResult = results[3];
        const countryRowsResult = results[4];
        const salesTrendResult = results[5];
        const failures = results
          .filter(function filterFailure(result) {
            return result.status === "rejected";
          })
          .map(function mapFailure(result) {
            return getMerchantApiErrorMessage(result.reason, "Merchant API request failed");
          });
        const summary = summaryResult.status === "fulfilled" ? summaryResult.value : null;
        const recentTransactions =
          recentTransactionsResult.status === "fulfilled"
            ? recentTransactionsResult.value
            : null;
        const paymentMethods =
          paymentMethodsResult.status === "fulfilled" ? paymentMethodsResult.value : null;
        const countries =
          countryRowsResult.status === "fulfilled" ? countryRowsResult.value : null;
        const topOutlets =
          topOutletsResult.status === "fulfilled" ? topOutletsResult.value : null;
        const salesTrend =
          salesTrendResult.status === "fulfilled" ? salesTrendResult.value : null;

        setDashboardState({
          kpis: buildDashboardKpis(summary, emptyState.kpis),
          chartData: buildChartDataFromApi(salesTrend),
          paymentSections: buildPaymentSectionsFromApi(paymentMethods),
          recentTransactions:
            recentTransactions && Array.isArray(recentTransactions.items)
              ? recentTransactions.items.map(normalizeMerchantApiTransaction)
              : [],
          countryRows: buildCountryRowsFromApi(countries),
          topOutlets: buildTopOutletsFromApi(topOutlets),
        });

        setPagePeriod(
          getMonthOptionLabel(
            toMerchantString(
              pickMerchantField(summary, ["period"]),
              toMerchantString(
                pickMerchantField(paymentMethods, ["period"]),
                toMerchantString(
                  pickMerchantField(topOutlets, ["period"]),
                  toMerchantString(
                    pickMerchantField(salesTrend, ["period"]),
                    selectedMonth || defaultMonthValue,
                  ),
                ),
              ),
            ),
          ),
        );

        if (failures.length) {
          setRequestState("error");
          setDataNote(
            "Merchant dashboard request finished, but some blocks are still missing backend data. Unavailable values stay as 0 or -. " +
              failures[0],
          );
        } else {
          setRequestState("success");
          setDataNote("Merchant dashboard request completed. Live API data is now displayed.");
        }
      }

      requestDashboardData().catch(function handleDashboardError(error) {
        if (!isActive) {
          return;
        }

        setDashboardState(emptyState);
        setRequestState("error");
        setDataNote(
          "Merchant dashboard request failed. Placeholder values are being shown until the API responds. " +
            getMerchantApiErrorMessage(error, ""),
        );
      });

      return function cleanup() {
        isActive = false;
      };
    },
    [defaultMonthValue, emptyState, selectedMonth],
  );

  function handleExport() {
    setIsExporting(true);

    try {
      downloadMerchantBlob(
        new Blob([buildDashboardExportContent(dashboardState)], {
          type: "text/csv;charset=utf-8",
        }),
        "merchant-dashboard-snapshot-" + new Date().toISOString().slice(0, 10) + ".csv",
      );
      setDataNote("Merchant dashboard snapshot exported from the current rendered values.");
    } finally {
      setIsExporting(false);
    }
  }

  const maxCountryGmv = dashboardState.countryRows[0] ? dashboardState.countryRows[0].gmv : 1;
  const totalCountryGmv = dashboardState.countryRows.reduce(function sumCountry(total, country) {
    return total + Number(country.gmv || 0);
  }, 0);
  const localCountryGmv = dashboardState.countryRows.reduce(function sumLocalCountry(total, country) {
    return country.code === "SG" ? total + Number(country.gmv || 0) : total;
  }, 0);
  const foreignCountryGmv = Math.max(0, totalCountryGmv - localCountryGmv);
  const businessName = toMerchantString(
    pickMerchantField(merchantUser, ["businessName", "companyName", "name"]),
    "Merchant Account",
  );

  return (
    <div className="merchant-page">
      <div className="merchant-page-header merchant-page-header-row">
        <div>
          <div className="merchant-page-title">Your Business Overview</div>
          <div className="merchant-page-subtitle">
            {pagePeriod} · {businessName}
          </div>
        </div>
        <div className="merchant-page-actions">
          <MerchantDatePickerField
            mode="month"
            minDate={earliestMonthDate}
            maxDate={latestMonthDate}
            placeholder="Select month"
            selected={getDashboardMonthDate(selectedMonth)}
            onChange={function handleMonthChange(date) {
              const nextMonthValue = getDashboardMonthValueFromDate(date);

              if (nextMonthValue) {
                setSelectedMonth(nextMonthValue);
              }
            }}
          />
          <button
            className="merchant-button merchant-button-outline"
            type="button"
            onClick={handleExport}
            disabled={isExporting || requestState === "loading"}
          >
            <MerchantDownloadIcon />
            {isExporting ? "Exporting..." : "Export"}
          </button>
        </div>
      </div>

      {requestState !== "success" ? (
        <div className="merchant-data-note">
          {dataNote}
          {requestState === "loading" ? (
            <div className="merchant-inline-status merchant-inline-status-loading">
              <span className="merchant-request-spinner" />
              Dashboard request in progress
            </div>
          ) : null}
          {requestState === "error" ? (
            <div className="merchant-inline-status merchant-inline-status-error">
              Request completed with missing data
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="merchant-kpi-grid">
        {dashboardState.kpis.map(function renderCard(kpi) {
          return (
            <MerchantStatCard
              key={kpi.label}
              stripeClassName={kpi.stripeClassName}
              label={kpi.label}
              value={kpi.value}
            >
              {renderMerchantMeta(kpi)}
            </MerchantStatCard>
          );
        })}
      </div>

      <div className="merchant-chart-grid">
        <div className="merchant-card">
          <div className="merchant-card-header">
            <div>
              <div className="merchant-card-title">Sales Trend</div>
              <div className="merchant-card-subtitle">Daily GMV · {pagePeriod}</div>
            </div>
            <div className="merchant-segmented">
              <button className="merchant-button merchant-button-primary" type="button">
                Daily
              </button>
              <button className="merchant-button merchant-button-outline" type="button" disabled>
                Weekly
              </button>
            </div>
          </div>
          <MerchantLineChart data={dashboardState.chartData} />
        </div>

        <div className="merchant-card merchant-scroll-card">
          <div className="merchant-card-header">
            <div>
              <div className="merchant-card-title">Payment Methods</div>
              <div className="merchant-card-subtitle">By GMV · {pagePeriod}</div>
            </div>
          </div>
          {dashboardState.paymentSections.length ? (
            dashboardState.paymentSections.map(function renderSection(section) {
              return (
                <div className="merchant-pay-section" key={section.title}>
                  {section.items.map(function renderItem(item) {
                    return (
                      <div className="merchant-pay-item" key={item.name}>
                        <div className="merchant-pay-item-row">
                          <div className="merchant-pay-scheme">
                            <span
                              className="merchant-scheme-dot"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="merchant-pay-name">{item.name}</span>
                          </div>
                          <span className="merchant-pay-percentage">{item.percentage}</span>
                        </div>
                        <div className="merchant-pay-bar">
                          <div
                            className="merchant-pay-fill"
                            style={{
                              width: item.width,
                              backgroundColor: item.color,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <div className="merchant-card-empty">No payment method data yet.</div>
          )}
        </div>
      </div>

      <div className="merchant-bottom-grid">
        <div className="merchant-card">
          <div className="merchant-card-header">
            <div>
              <div className="merchant-card-title">Recent Transactions</div>
            </div>
            <Link className="merchant-text-link" to="/transactions">
              View all →
            </Link>
          </div>
          <div className="merchant-mobile-compact-list">
            {dashboardState.recentTransactions.length ? (
              dashboardState.recentTransactions.map(function renderCompactTransaction(transaction) {
                const transactionId = toMerchantString(
                  pickMerchantField(transaction, ["id", "txnId"]),
                  "",
                );
                const status = getMerchantStatus(transaction);

                return (
                  <button
                    className="merchant-mobile-compact-item"
                    key={transactionId}
                    type="button"
                    onClick={function openTransactionDetail() {
                      history.push("/transactions/" + transactionId);
                    }}
                  >
                    <div className="merchant-mobile-compact-main">
                      <div className="merchant-mobile-compact-title">
                        {toMerchantString(
                          pickMerchantField(transaction, ["storeName", "store"]),
                          "Unknown Store",
                        ).replace("@", " · ")}
                      </div>
                      <div className="merchant-mobile-compact-subtitle">
                        {toMerchantString(
                          pickMerchantField(transaction, ["capturedTime", "creationTime", "createdAt"]),
                          "-",
                        )}
                      </div>
                    </div>
                    <div className="merchant-mobile-compact-side">
                      <div className="merchant-mobile-compact-value">
                        {formatMerchantCurrency(pickMerchantField(transaction, ["amount"], 0), 2)}
                      </div>
                      <MerchantStatusBadge
                        status={status}
                        label={getMerchantStatusLabel(status)}
                      />
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="merchant-card-empty">No recent transaction data yet.</div>
            )}
          </div>
          <div className="merchant-table-wrap merchant-desktop-table-wrap">
            <table className="merchant-table">
              <thead>
                <tr>
                  <th>Outlet</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardState.recentTransactions.length ? (
                  dashboardState.recentTransactions.map(function renderRow(transaction) {
                    const storeName = toMerchantString(
                      pickMerchantField(transaction, ["store", "storeName"]),
                      "Unknown Store",
                    );
                    const amount = pickMerchantField(transaction, ["amount"], 0);
                    const method = getMerchantMethod(transaction);
                    const status = getMerchantStatus(transaction);

                    return (
                      <tr
                        key={toMerchantString(
                          pickMerchantField(transaction, ["id", "txnId"]),
                          storeName,
                        )}
                        className="merchant-clickable-row"
                        onClick={function openTransactionDetail() {
                          history.push(
                            "/transactions/" +
                              toMerchantString(
                                pickMerchantField(transaction, ["id", "txnId"]),
                                "",
                              ),
                          );
                        }}
                      >
                        <td>{formatOutletDisplayName(storeName)}</td>
                        <td className="merchant-mono">{formatMerchantCurrency(amount)}</td>
                        <td>
                          <span className="merchant-table-chip">{method}</span>
                        </td>
                        <td>
                          <MerchantStatusBadge
                            status={status}
                            label={getMerchantStatusLabel(status)}
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="merchant-table-empty" colSpan="4">
                      No recent transaction data yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="merchant-card">
          <div className="merchant-card-header">
            <div>
              <div className="merchant-card-title">Card Issuing Countries</div>
              <div className="merchant-card-subtitle">
                Where your customers&apos; cards were issued
              </div>
            </div>
            <Link className="merchant-text-link" to="/outlets">
              Outlets →
            </Link>
          </div>
          <div className="merchant-pay-section">
            <div className="merchant-pay-section-label">Top Issuing Countries · Payment Card GMV</div>
          </div>
          <div className="merchant-country-list">
            {dashboardState.countryRows.length ? (
              dashboardState.countryRows.map(function renderCountry(country) {
                return (
                  <div className="merchant-country-item" key={country.code}>
                    <span className="merchant-country-flag">{country.flag}</span>
                    <span className="merchant-country-name">{country.name}</span>
                    <div className="merchant-country-bar-wrap">
                      <div
                        className="merchant-country-bar-fill"
                      style={{
                        width:
                          (
                            (Number(country.gmv || 0) / (maxCountryGmv || 1)) *
                            100
                          ).toFixed(2) + "%",
                      }}
                    />
                  </div>
                  <span className="merchant-country-percentage">
                    {formatMerchantPercent(
                      (Number(country.gmv || 0) / (totalCountryGmv || 1)) * 100,
                      2,
                      "0.00%",
                    )}
                  </span>
                  </div>
                );
              })
            ) : (
              <div className="merchant-card-empty">No issuer country data yet.</div>
            )}
          </div>
          {dashboardState.countryRows.length ? (
            <div className="merchant-transaction-summary-stats" style={{ marginTop: 20 }}>
              <div className="merchant-transaction-summary-stat">
                <div className="merchant-transaction-summary-stat-label">
                  Local cards (SG-issued)
                </div>
                <div className="merchant-transaction-summary-stat-value merchant-mono">
                  {formatMerchantPercent(
                    ((localCountryGmv || 0) / (totalCountryGmv || 1)) * 100,
                    2,
                    "0.00%",
                  )}
                </div>
              </div>
              <div className="merchant-transaction-summary-stat">
                <div className="merchant-transaction-summary-stat-label">
                  Foreign-issued cards
                </div>
                <div className="merchant-transaction-summary-stat-value merchant-mono">
                  {formatMerchantPercent(
                    ((foreignCountryGmv || 0) / (totalCountryGmv || 1)) * 100,
                    2,
                    "0.00%",
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="merchant-card">
        <div className="merchant-card-header">
          <div>
            <div className="merchant-card-title">Top Outlets by GMV</div>
            <div className="merchant-card-subtitle">{pagePeriod}</div>
          </div>
          <Link className="merchant-text-link" to="/outlets">
            View all →
          </Link>
        </div>
        <div className="merchant-outlet-list">
          {dashboardState.topOutlets.length ? (
            dashboardState.topOutlets.map(function renderOutlet(outlet, index) {
              return (
                <div className="merchant-outlet-item" key={outlet.id || outlet.name}>
                  <div
                    className={
                      "merchant-outlet-rank" +
                      (index === 0 ? " top-1" : index < 3 ? " top-3" : "")
                    }
                  >
                    {index + 1}
                  </div>
                  <div className="merchant-outlet-info">
                    <div className="merchant-outlet-name">
                      {formatOutletDisplayName(toMerchantString(outlet.name, "Unknown Outlet"))}
                    </div>
                  </div>
                  <div className="merchant-outlet-summary">
                    <div className="merchant-outlet-value">
                      {formatMerchantCompactCurrency(Number(outlet.gmv || 0))}
                    </div>
                    <div className="merchant-outlet-subtext">
                      {Number(outlet.transactions || outlet.txn || 0).toLocaleString("en-SG")} txns
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="merchant-card-empty">No outlet ranking data yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
